<template>
  <div v-if="active" class="tour-overlay" role="dialog" aria-modal="true">
    <div class="tour-spotlight" :style="spotlightStyle"></div>
    <div class="tour-popover" :style="popoverStyle">
      <template v-if="index === 0">
        <h2 class="tour-intro-title">Novidades</h2>
        <div class="tour-intro-subtitle">{{ current.title }}</div>
      </template>
      <template v-else>
        <div class="tour-step-index">Passo {{ index + 1 }} de {{ steps.length }}</div>
        <h3 class="tour-title">{{ current.title }}</h3>
      </template>
      <!-- texto pode trazer <strong>/<em>; sem injeção de HTML externo -->
      <p class="tour-text" v-html="current.text"></p>
      <div class="tour-actions">
        <button type="button" class="tour-btn tour-btn--text" @click="finish(true)">Pular</button>
        <div>
          <button v-if="index > 0" type="button" class="tour-btn tour-btn--ghost" @click="prev">Anterior</button>
          <button type="button" class="tour-btn tour-btn--primary" @click="next">
            {{ index === steps.length - 1 ? 'Concluir' : 'Próximo' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'applicationsListTourSeen';
const VERSION = 'v1';

export default {
  name: 'ApplicationsListTour',
  data() {
    return {
      active: false,
      index: 0,
      spotlightStyle: { display: 'none' },
      popoverStyle: { display: 'none' },
      steps: [
        {
          target: '.status-cards',
          title: 'Cards de status',
          text: 'Os 6 contadores no topo agora funcionam como <strong>filtros rápidos</strong>: clique em um card para ver só os requerimentos daquele status. A fila aguardando (<em>A ANALISAR</em>) e o que já está em andamento (<em>EM ANÁLISE</em>) ficam em cards distintos.',
        },
        {
          target: '.tour-filters',
          title: 'Barra de filtros',
          text: 'Combine <strong>Tipo</strong>, <strong>Tipologia</strong>, <strong>Analista</strong> (basta começar a digitar para ver as opções), <strong>Boleto</strong> e <strong>Período</strong>. Os filtros se somam ao card de status, e o botão <strong>Limpar</strong> apaga tudo de uma vez.',
        },
        {
          target: '.elevation-1 thead',
          title: 'Tabela enriquecida',
          text: 'Duas colunas novas: <strong>Analista</strong> (mostra quem já pegou o requerimento) e <strong>Criado em</strong> (data de entrada). Clique nos cabeçalhos <strong>ID</strong> ou <strong>Criado em</strong> para ordenar a lista.',
        },
        {
          target: '.elevation-1 tbody tr:first-child td:last-child',
          title: 'Abrir em nova aba',
          text: 'O segundo ícone (↗) abre o detalhe em uma <strong>nova aba</strong>, sem perder a posição da lista nem os filtros. O olho continua navegando na mesma aba como antes.',
        },
      ],
    };
  },
  computed: {
    current() {
      return this.steps[this.index] || {};
    },
  },
  methods: {
    maybeStart() {
      if (typeof window === 'undefined') return;
      try {
        if (window.localStorage.getItem(STORAGE_KEY) === VERSION) return;
      } catch (e) { /* localStorage indisponível -> mostra mesmo assim */ }
      this.start();
    },
    start() {
      this.index = 0;
      this.active = true;
      this.$nextTick(() => this.position());
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('keydown', this.handleKey);
    },
    next() {
      if (this.index === this.steps.length - 1) return this.finish(false);
      this.index += 1;
      this.position();
    },
    prev() {
      if (this.index === 0) return;
      this.index -= 1;
      this.position();
    },
    finish(skipped) {
      this.active = false;
      try {
        window.localStorage.setItem(STORAGE_KEY, VERSION);
      } catch (e) { /* quota / privado -> ignora */ }
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('keydown', this.handleKey);
      this.$emit(skipped ? 'skipped' : 'finished');
    },
    handleResize() {
      if (this.active) this.position();
    },
    handleKey(e) {
      if (!this.active) return;
      if (e.key === 'Escape') this.finish(true);
      else if (e.key === 'ArrowRight' || e.key === 'Enter') this.next();
      else if (e.key === 'ArrowLeft') this.prev();
    },
    position() {
      this.$nextTick(() => {
        const step = this.current;
        const el = step && step.target ? document.querySelector(step.target) : null;
        const popW = 360;
        const popH = 220;
        const gap = 14;

        if (!el) {
          // Alvo ausente (ex.: lista vazia) — centraliza o popover, sem spotlight
          this.spotlightStyle = { display: 'none' };
          this.popoverStyle = {
            top: `${Math.max(20, (window.innerHeight - popH) / 2)}px`,
            left: `${Math.max(20, (window.innerWidth - popW) / 2)}px`,
          };
          return;
        }

        let rect = el.getBoundingClientRect();
        // Só rola quando o alvo está totalmente fora da viewport — instantâneo, pra re-ler
        // o rect na posição final (evita spotlight no lugar errado durante scroll suave).
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
          el.scrollIntoView({ block: 'start' });
          window.scrollBy(0, -80); // folga acima
          rect = el.getBoundingClientRect();
        }

        const pad = 8;
        // Limita o spotlight à altura da viewport: evita dim "infinito" em alvos compridos.
        const maxH = window.innerHeight - 16;
        const spotTop = Math.max(8, rect.top - pad);
        const spotHeight = Math.min(rect.height + pad * 2, maxH);
        const spotLeft = Math.max(8, rect.left - pad);
        this.spotlightStyle = {
          top: `${spotTop}px`,
          left: `${spotLeft}px`,
          width: `${rect.width + pad * 2}px`,
          height: `${spotHeight}px`,
          display: 'block',
        };

        // Popover: abaixo do spotlight quando cabe, senão acima; clampado horizontalmente.
        const spotBottom = spotTop + spotHeight;
        let popTop = spotBottom + gap;
        if (popTop + popH > window.innerHeight - 12) popTop = Math.max(12, spotTop - popH - gap);
        const popLeft = Math.max(12, Math.min(window.innerWidth - popW - 12, rect.left));
        this.popoverStyle = { top: `${popTop}px`, left: `${popLeft}px` };
      });
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKey);
  },
};
</script>

<style scoped lang="scss">
.tour-overlay {
  position: fixed;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 10000;
  /* Bloqueia interação com o app por trás; só o popover e seus botões respondem */
  pointer-events: auto;
}
.tour-spotlight {
  position: fixed;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  pointer-events: none;
  transition: top .25s ease, left .25s ease, width .25s ease, height .25s ease;
}
.tour-popover {
  position: fixed;
  width: 360px;
  background: #fff;
  border-radius: 10px;
  padding: 18px 18px 14px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
  font-family: inherit;
  z-index: 10001;
  transition: top .25s ease, left .25s ease;
}
.tour-step-index {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.tour-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
}
.tour-intro-title {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: #1976d2;
  letter-spacing: -0.3px;
  line-height: 1.15;
}
.tour-intro-subtitle {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  margin-bottom: 14px;
}
.tour-text {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #444;
}
.tour-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.tour-btn {
  border: none;
  background: transparent;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  &--text {
    color: #777;
    &:hover { color: #444; }
  }
  &--ghost {
    color: #1976d2;
    margin-right: 6px;
    &:hover { background: rgba(25, 118, 210, 0.08); }
  }
  &--primary {
    background: #1976d2;
    color: #fff;
    &:hover { background: #1565c0; }
  }
}
</style>
